import * as React from 'react';
import { useState } from 'react';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { Col, Input, Label, Row, Table } from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../../hooks/index';
import {
    Afr, bulkReassignColumnDimension, changeColumnDimension, FactTable, getDimensionForFact,
    getRowConceptFromTable, getSelectedAfr, getTable
} from '../../redux/AfrSlice';
import { Concept, loadConcept } from '../../redux/ConceptSlice';
import { setIsDirty } from '../../redux/MiscSlice';
import { ErrorSummaryAFRSection, getErrorList } from '../../redux/ValidationSlice';
import enums from '../../utils/enums';
import utilityHelper from '../../utils/utilityHelper';
import { TaxonomyTypeEnum } from '../../redux/TaxonomyVersionSlice';
import { AdminUserRoleEnum, selectAdminUserRole } from '../../redux/UserSlice';



const SupplementalEditorGrid = (props: {
    tableName: string,
    affiliateCode: string,
    gridData: any[],
    gridColumns: any[],
    gridOptions: any,
    gridId: string,
}) => {

    /* ************************************** Redux ********************************************/
    const dispatch = useAppDispatch();
    const errorList = useAppSelector(getErrorList);

    /* ************************************** Modal section ************************************/

    const tabulatorRef = React.useRef<any>();
    const currentAfr: Afr | null = useAppSelector(getSelectedAfr);
    const adminUserRole: AdminUserRoleEnum | null = useAppSelector(selectAdminUserRole);

    const canUseRow = function (row: any, affiliateCode: string): boolean {
        var rowDimMap: any = {};
        for (var drMap of row.dimensions) {
            rowDimMap[drMap.axis.id] = drMap;
        }
        // If we have an affiliate code then we are representing facts for one affiliate, to filter for those fact rows that match the current affiliate
        if (rowDimMap.hasOwnProperty("ReportingUnitAxis")) {
            if (!props.affiliateCode || props.affiliateCode.length === 0) {
                return false;
            }
            if (rowDimMap["ReportingUnitAxis"].member.code !== props.affiliateCode) {
                return false;
            }
        }
        if (!row.account) {
            return false;
        }

        return true;

    }

    const getInitialPayPeriod = function (ctxt: string): string {
        var result: string = "PP_UnspecifiedMember";
        if (!currentAfr) {
            return result;
        }
        var acctTable = getTable(currentAfr, props.tableName);
        if (!acctTable) {
            return result;
        }
        for (var row of acctTable.rows) {
            if (!canUseRow(row, props.affiliateCode)) {
                continue;
            }
            for (var f of row.facts) {
                var ppDim = getDimensionForFact(f, "CompensationCategoryAxis");
                if (ppDim) {
                    var ppDimId = ppDim.id;
                    if (ppDimId !== "ContractorMember") {
                        result = ppDimId;
                    }
                }
            }
        }
        return result;
    }

    const [payPeriod, setPayPeriod] = useState(getInitialPayPeriod("basic"));



    /* ***********************************************************************************************************/
    //Events
    /* ***********************************************************************************************************/
    const numberFormatter = function (cell: any) {

        var row = cell.getRow();
        var dataType: string = row.getData().dataType;
        
        
        if (props.gridId == 'independentCompensation' && dataType == 'XBRLI_MONETARYITEMTYPE') {
            var formatted = utilityHelper.Formatter(cell.getValue(), dataType, 0);
        } else {
            var formatted = utilityHelper.Formatter(cell.getValue(), dataType);
        }
        return formatted;

    }


    const showErrorIcon = function (cell: any, formatterParams: any, onRendered: any) {
        var text: string = cell.getValue();
        var { section, tab } = utilityHelper.getSectionError(props.tableName);

        if ((typeof text) === "undefined") {
            return text;
        }

        // Verify if there is an error in this section
        var errorSection: ErrorSummaryAFRSection | undefined = errorList.find((x: any) => x.section.sectionCode === section);
        if ((typeof errorSection) === "undefined") {
            return text;
        }

        if (errorSection != undefined) {
            // Discover the array of errors for the section
            var error: any | undefined = errorSection.errorList.find(x => x.entityType === tab && x.entityCode === cell.getData().accountCode);

            if ((typeof error) === "undefined") {
                return text;
            } else {
                if (error != undefined) {
                    return "<i class='bi bi-x-octagon-fill text-danger' title='" + error.description + "'></i> " + text;
                }
            }
        } else {
            return text;
        }
    };


    const handleCellClick = function (e: any, cell: any) {

        var row = cell.getRow();

        if (row._row.type != "row") { return }

        var rowId = row.getData().accountCode;
        var accountId = row.getData().accountId;


        if (accountId) {
            dispatch(loadConcept({
                conceptId: accountId,
                taxonomyType: currentAfr ? currentAfr.taxonomyType : TaxonomyTypeEnum.AFR,
                afrYear: currentAfr ? currentAfr.year : 2022
            }));
        }

    }


    const handleRowClick = function (e: any, row: any) {
        //e - the click event object
        //row - row component
        if (row._row.type != "row") { return }

        row.getTable().deselectRow();
        row.select();
    }



    // logic to handle the dirty flag in redux store
    const handleEdited = function (cell: any) {

        var newValue: string = cell.getValue();
        newValue = newValue.trim();

        if ((getValue(cell.getOldValue()) != getValue(newValue)) || (newValue.length > 0 && getValue(newValue) === 0)) {
            // Turn ON the dirty flag in redux
            dispatch(setIsDirty({ IsDirty: true, Page: "" }));
            //setDataKey(cell.getRow().getData().accountCode)
            // get next cell to right
            //setFieldToEdit(getNextCell(cell));
            updateCell(cell);
        }

    }

    const handleXChange = function (event: any) {
        var value: string = event.target.value
        var activeField = payPeriod;
        setPayPeriod(value);
        var aid: string = "";
        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);
            if (acctTable && acctTable.structure.columnHierarchy.axes) {
                aid = acctTable.structure.columnHierarchy.axes[0].id;
            }
        }
        changeDimensionValue(aid, activeField, value);
        setPayPeriodOptions(getPayPeriodOptions(payPeriod));
    }

    const updatePayPeriod = function (value: string) {
        setPayPeriod(value);
        var activeField = value;
        setPayPeriodOptions(getPayPeriodOptions(value));
    }
    const handleChange = function (event: any) {
        var value: string = event.target.value
        var prevDimensionId = payPeriod;
        var dimensionId = value;
        var axId = "CompensationCategoryAxis";
        if (dimensionId == prevDimensionId) {
            return;
        }
        var acctTable : any = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);
        }
        if (!acctTable) {
            // We have no afr or don't have the compensation table.
            // Just set the pay period options and return
            updatePayPeriod(value);
            return;
        }

        dispatch(setIsDirty({ IsDirty: true, Page: "" }));
        utilityHelper.saveDeferredChanges(dispatch);

        updatePayPeriod(value);

        var changeFactDimension: bulkReassignColumnDimension = {
            tableId: props.tableName,
            sliceId: props.affiliateCode,
            axisId: axId,
            prevId: prevDimensionId,
            newId: dimensionId
        }
        dispatch(changeColumnDimension(changeFactDimension));

    }

    const updateCell = function (cell: any) {

        var table = cell.getRow().getTable();
        var rdata = cell.getRow().getData();

        // Track the changes on Cells to an array of facts into MiscSlice
        if (props.affiliateCode) {
            var rowKey = props.tableName + "^" + props.affiliateCode;
            // NOTE: the props.affiliateCode appears to be stale sometimes, and unreliable - so facts get assigned to the wrong data
            // row in the afr slice. I have add an 'affiliateCode' attribute to each row when the data is being constructed, and use that
            // in preference to a potentially out-of-sync property.
            // This is not a 'wrong' thing to do and the affiliate code is correct each time the table data is built.
            if (rdata.hasOwnProperty("affiliateCode")) {
                rowKey = props.tableName + "^" + rdata.affiliateCode;
            }
            utilityHelper.updateTableCell(rowKey, null, cell, table, dispatch);
        } else {
            utilityHelper.updateTableCell(props.tableName, null, cell, table, dispatch);
        }
        // ***************************************************************************

    }
    const changeDimensionValue = function (axId: string, prevDimensionId: string, dimensionId: string) {
        if (tabulatorRef === null || !tabulatorRef.current === null) {
            return;
        }
        var tabulatorTable = tabulatorRef.current.table;
        var factCells : any[] = [];
        var rows = tabulatorTable.getData();
        for (var dataRow of rows) {
            var updatedFacts = utilityHelper.getRowDimensionFacts(props.tableName, dataRow, null);
            for (var fact of updatedFacts) {
                var fText = JSON.stringify(fact).replace(prevDimensionId, dimensionId);
                var modifiedFact : any = JSON.parse(fText);
                factCells.push(modifiedFact);
            }
        }
        var changeFactDimension: bulkReassignColumnDimension = {
            tableId: props.tableName,
            sliceId: props.affiliateCode,
            axisId: axId,
            prevId: prevDimensionId,
            newId: dimensionId
        }
        dispatch(setIsDirty({ IsDirty: true, Page: "" }));
        utilityHelper.saveDeferredChanges(dispatch);
        dispatch(changeColumnDimension(changeFactDimension));
    }



    // ************************************************************* /*
    // General purpose functions                                     //
    // ************************************************************* /*
    // get next row to right to be edited
    const getNextCell = function (cell: any) {
        if (!cell) { return "" };
        var cells: any[] = cell.getRow().getCells();
        var found = false;
        for (var c of cells) {
            if (found) { return c.getField() }
            if (c.getField() === cell.getField()) { found = true; }
        }
        return "";
    }


    // Helper to get a cell value
    const getValue = (e: any) => {
        var result = parseInt(e);
        if (isNaN(result)) return 0;
        return result;

    }

    // ************************************************************* /*
    // Default columns                                               //
    // ************************************************************* /*
    const createColumns = function (acctTable: FactTable | null, payp: string) {
        /* PAINTING THE COLUMNS */
        const defaultColumns: any = [];

        defaultColumns.push({
            title: "",
            field: "account",
            headerSort: false,
            width: 350,
            formatter: showErrorIcon,
            frozen: true,
            headerHozAlign: "center",
        });

        if (currentAfr) {
            var isLocked = utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin);

            if (acctTable) {
                var columnVisibilityMap: any = {
                    "ContractorMember": true
                };
                columnVisibilityMap[payp] = true;
                var bUseFirstColumn: boolean = false;
                for (var row of acctTable.rows) {
                    if (!canUseRow(row, props.affiliateCode)) {
                        continue;
                    }
                    for (var f of row.facts) {
                        for (var dr of f.dimensions) {
                            if (dr.member != null) {
                                var currentPayPeriod = dr.member.id;
                                if (currentPayPeriod != "ContractorMember") {
                                    columnVisibilityMap[currentPayPeriod] = true;
                                    bUseFirstColumn = false;
                                }
                            }
                        }
                    }
                }
                // Note - it is possible for the requested account table to not be present in this AFR
                // However that should be deal with upstream so the option doe snot appear
                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    var t : any = null;
                    if (tbeGroup.sublevels) {
                        var filterOptions: any = {};
                        for (var tbeCol of tbeGroup.sublevels) {
                            if (tbeCol.dimensionaspect.member != null) {
                                t = tbeCol.dimensionaspect.member.id;
                            } else {
                                t = null;
                            }

                            if (bUseFirstColumn) {
                                if (t != null) {
                                    columnVisibilityMap[t] = true;
                                }

                                bUseFirstColumn = false;
                            }
                            if (isLocked) {
                                defaultColumns.push({
                                    title: tbeCol.label,
                                    field: t,
                                    headerSort: false,
                                    validator: "numeric",
                                    headerHozAlign: "center",
                                    hozAlign: "right",
                                    formatter: numberFormatter,
                                    cellClick: handleCellClick,
                                    visible: columnVisibilityMap.hasOwnProperty(t)
                                });
                            } else {
                                defaultColumns.push({
                                    title: tbeCol.label,
                                    field: t,
                                    headerSort: false,
                                    editor: "input",
                                    editorParams: { mask: "*999999999999" },
                                    validator: "numeric",
                                    headerHozAlign: "center",
                                    hozAlign: "right",
                                    formatter: numberFormatter,
                                    cellEdited: handleEdited,
                                    cellClick: handleCellClick,
                                    visible: columnVisibilityMap.hasOwnProperty(t)
                                });
                            }

                        }

                    } else {
                        if (tbeGroup.dimensionaspect.member != null) {
                            t = tbeGroup.dimensionaspect.member.id;
                        } else {
                            t = null;
                        }

                        if (isLocked) {
                            defaultColumns.push({
                                title: tbeGroup.label,
                                field: t,
                                headerSort: false,
                                validator: "numeric",
                                headerHozAlign: "center",
                                hozAlign: "right",
                                cellClick: handleCellClick,
                                formatter: numberFormatter,
                            });
                        } else {
                            defaultColumns.push({
                                title: tbeGroup.label,
                                field: t,
                                headerSort: false,
                                editor: "input",
                                editorParams: { mask: "*999999999999" },
                                validator: "numeric",
                                headerHozAlign: "center",
                                hozAlign: "right",
                                cellEdited: handleEdited,
                                cellClick: handleCellClick,
                                formatter: numberFormatter,
                            });
                        }

                    }

                }
            }
        }
        return defaultColumns
    }
    const [compensationColumns, setCompensationColumns] = useState(createColumns(getTable(currentAfr, props.tableName), getInitialPayPeriod("columns")));



    // ************************************************************* /*
    // Radio Button Options                                          //
    // ************************************************************* /*
    interface PayPeriodOption {
        label: string;
        code: string;
        selected: boolean;
    }
    const getPayPeriodOptions = function (currentPayPeriod: string): PayPeriodOption[] {
        var options: PayPeriodOption[] = [];
        var acctTable: FactTable | null = null;
        if (currentAfr) {
            acctTable = getTable(currentAfr, props.tableName);
            if (acctTable) {
                for (var tbeGroup of acctTable.structure.nthlevelcolumnHierarchy) {
                    var t : any = null;
                    if (tbeGroup.sublevels) {
                        for (var tbeCol of tbeGroup.sublevels) {
                            if (tbeCol.dimensionaspect.member != null) {
                                var mbr = tbeCol.dimensionaspect.member;
                                t = tbeCol.dimensionaspect.member.id;
                                if (mbr != null && mbr.id === currentPayPeriod) {
                                    options.push({ label: mbr.name ? mbr.name : mbr.code, code: mbr.id, selected: true });
                                } else {
                                    options.push({ label: mbr.name ? mbr.name : mbr.code, code: mbr.id, selected: false });
                                }
                            }
                        }
                    }
                }
            }
        }
        return options;
    }


    // ************************************************************* /*
    // Grid Options                                                  //
    // ************************************************************* /*
    const newGridOptions = {
        layout: "fitColumns",
        layoutColumnsOnNewData: true,
        selectable: 1,
        selectableRollingSelection: false,
        scrollToRowPosition: "center",
        height: "300",  // to be adjusted if another thing is decided
        index: "account",
        cellEdited: handleEdited,
        cellClick: handleCellClick,
        ...props.gridOptions

    }




    /* PAINTING THE ROWS */
    const createTableData = function (acctTable: FactTable | null) {
        var tabledata: any = [];
        if (acctTable) {

            for (var row of acctTable.rows) {
                if (!canUseRow(row, props.affiliateCode)) {
                    continue;
                }
                if (row.account === null) {
                    // TODO - we have an error
                    continue;
                }
                var rowid = props.tableName + "|" + row.account.code;
                var accountLabel = row.account.name;
                var accountId = row.account.id;
                var rowConcept: Concept | null = getRowConceptFromTable(acctTable, row.account.code);
                var dataType: string = "none";

                if (rowConcept) {
                    dataType = rowConcept.datatype;
                }

                var nextRow: any = {
                    id: rowid, account: accountLabel, rowConceptId: accountId, accountId: accountId, accountCode: row.account.code, dataType: dataType
                }
                if (props.affiliateCode) {
                    nextRow.affiliateCode = props.affiliateCode;
                }

                /* If any fact exist then it find the column dimension value */
                for (var fact of row.facts) {
                    var colDimValue = utilityHelper.getFirstDimensionNotMatchingCode(fact, props.affiliateCode);
                    var colcode;
                    if (colDimValue != null && colDimValue.member != null && colDimValue) {
                        colcode = colDimValue.member.id;
                    } else {
                        // TODO - There is an error here, post to error handling
                        colcode = "error";
                    }
                    nextRow[colcode] = fact.value;
                }
                tabledata.push(nextRow);
            }
        }
        return tabledata;

    }
    const [compensationData, setCompensationData] = useState(createTableData(getTable(currentAfr, props.tableName)));


    //                                            disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)}

    React.useEffect(() => {

        if (payPeriodOptions == undefined) {
            setPayPeriodOptions(getPayPeriodOptions(getInitialPayPeriod("props")))
        }

        let tabulator = new Tabulator("#" + props.gridId, {
            data: compensationData,
            columns: compensationColumns,
            layout: "fitColumns",
            layoutColumnsOnNewData: true,
            index: "account",
            selectable: 1,
            selectableRollingSelection: false,
            scrollToRowPosition: "center",

        }).on("rowClick", handleRowClick);

        tabulatorRef.current = tabulator;

        // reset the states when destroying the component
        return function cleanup() {

            // Saving pending changes to AfrSlice
            utilityHelper.saveDeferredChanges(dispatch);

        }
    }, []);


    React.useEffect(() => {
        // Saving pending changes to AfrSlice
        utilityHelper.saveDeferredChanges(dispatch);
        setPayPeriod(getInitialPayPeriod("basic"));
    }, [props.affiliateCode]);

    React.useEffect(() => {
        var tableData = createTableData(getTable(currentAfr, props.tableName));
        setCompensationData(tableData);
        var tableColumns = createColumns(getTable(currentAfr, props.tableName), payPeriod);
        setCompensationColumns(tableColumns);
        let tabulator = new Tabulator("#" + props.gridId, {
            data: tableData,
            columns: tableColumns,
            layout: "fitColumns",
            layoutColumnsOnNewData: true,
            index: "account",
            selectable: 1,
            selectableRollingSelection: false,
            scrollToRowPosition: "center",

        }).on("rowClick", handleRowClick);

    }, [payPeriod,props.affiliateCode]);

    const [payPeriodOptions, setPayPeriodOptions] = React.useState<PayPeriodOption[]>(getPayPeriodOptions(getInitialPayPeriod("options")));

    return (
        <Row>
            <Col md={9}>
                <div id={props.gridId} />
            </Col>
            <Col>
                <Table bordered>
                    <thead>
                        <tr key={utilityHelper.generateGuid()} >
                            <th className="card-header" style={{
                                color: "white",
                                backgroundColor: "#458BCA",
                                borderRadius: "0px",
                                fontWeight: "initial"
                            }}>Choose an employee pay period {((props.affiliateCode && props.affiliateCode.length > 0) ? "(" + props.affiliateCode + ")" : "")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {payPeriodOptions && payPeriodOptions.map(function (item: PayPeriodOption) {
                            return <tr key={utilityHelper.generateGuid()}><td>
                                <Label className="ml-4" check>
                                    <Input type="radio"
                                        name="radioPayPeriod"
                                        value={item.code}
                                        disabled={utilityHelper.checkStatusList(currentAfr, enums.verifiedCertifiedRecertified)
                                            || (adminUserRole === AdminUserRoleEnum.Reviewer || adminUserRole === AdminUserRoleEnum.Admin)}
                                        onChange={handleChange}
                                        checked={item.code == payPeriod}
                                    /><b>{item.label}</b>
                                </Label>
                            </td></tr>
                        })}
                    </tbody>
                </Table>
            </Col>

        </Row>


    );
}


export default SupplementalEditorGrid;